import type { CarInput } from '../../shared/car.js';
import { t } from '../i18n/i18n.js';
import { el, icon } from './dom.js';
import type { IconName } from './icons.js';
import './touchControls.css';

/**
 * On-screen driving controls for touch devices: a floating-anchor steering pad, throttle
 * and brake pedals, a handbrake, and the keyboard actions that matter enough on a
 * phone to get a button of their own — respawn, camera toggle and look-back.
 *
 * This is the only place touch and the game meet. `InputController` merges whatever this
 * class reports with the keyboard, `Game` wires the two button taps to its own methods, and
 * nothing else in the client needs to know a touchscreen exists.
 */

/** The bits of `Window` detection actually reads, so a test can hand in a plain object
 *  instead of needing a real DOM. */
export interface TouchDetectionEnv {
  location: { search: string };
  matchMedia?: (query: string) => { matches: boolean };
  navigator?: { maxTouchPoints?: number };
}

/**
 * No single signal identifies a touch device reliably: `pointer: coarse` misses Windows
 * touchscreens that also have a mouse attached, and `ontouchstart` exists on plenty of
 * desktop Chrome installs with an unused digitizer. Both, ORed, catch what either misses
 * alone — and a false positive here just adds a control layer next to a keyboard that still
 * works, where a false negative leaves a phone with no way to drive at all. The asymmetry is
 * why this leans permissive.
 *
 * `?touch=1` / `?touch=0` overrides both, for the e2e suite and for checking the layout on
 * a desktop browser without devtools' emulation flipping `matchMedia`.
 */
export function shouldShowTouchControls(win: TouchDetectionEnv = window): boolean {
  const override = new URLSearchParams(win.location.search).get('touch');
  if (override === '1') return true;
  if (override === '0') return false;
  const coarse = win.matchMedia?.('(pointer: coarse)').matches ?? false;
  const touchCapable = 'ontouchstart' in win || (win.navigator?.maxTouchPoints ?? 0) > 0;
  return coarse || touchCapable;
}

/**
 * Pixels of horizontal thumb travel the stick treats as full lock, and the dead zone inside
 * that no steering comes from at all.
 *
 * 110 px is a hand-width thumb stroke on a phone held in landscape — short enough that full
 * lock doesn't need the thumb to leave a comfortable resting spot, long enough that the
 * first few degrees of steer aren't one over-sensitive millimetre. 6 px absorbs the jitter a
 * finger produces just resting on glass (a few px on every capacitive panel tested this way)
 * without eating a real, deliberate nudge, which the deliberately-narrow 110 px range would
 * otherwise make hard to place precisely.
 */
const STEER_RANGE_PX = 110;
const STEER_DEADZONE_PX = 6;

/** Maps a raw pixel displacement to -1..1 with a dead zone, so the last mile out of it isn't
 *  a jump from 0 to some nonzero minimum. */
export function applyDeadzone(dx: number, deadZone: number, range: number): number {
  const magnitude = Math.abs(dx);
  if (magnitude <= deadZone) return 0;
  const sign = Math.sign(dx);
  return sign * Math.min(1, (magnitude - deadZone) / (range - deadZone));
}

export interface TouchControlsOptions {
  onRespawn(): void;
  onToggleCamera(): void;
  /** The horn. There is no `H` key to press on a phone. */
  onHonk(): void;
  /** The Escape menu, for a phone, which has no Escape: the settings, and leaving. */
  onMenu(): void;
}

/**
 * `setPointerCapture` can throw — `InvalidPointerId` if the browser never saw this pointer
 * become active, which is rare in practice but not impossible on a device shedding touch
 * events under load. Capture is what keeps a control tracking a finger that wanders off its
 * bounds; losing it degrades to relying on the browser's normal hit-testing rather than
 * breaking the control; it is not worth losing the state update over. Every `onDown` below
 * sets its own state *before* calling this, so a throw here never leaves a button stuck.
 */
function capturePointer(target: Element, pointerId: number): void {
  try {
    target.setPointerCapture(pointerId);
  } catch {
    // See above — the control still works, just without capture.
  }
}

/**
 * A momentary on-screen button: tracks exactly one finger, ignores any other pointer that
 * lands on it while held, and reports whether it is currently down.
 */
class HoldButton {
  readonly el: HTMLButtonElement;
  pressed = false;
  private pointerId: number | null = null;

  constructor(className: string, id: string, glyph: IconName, label: string) {
    this.el = el('button', { class: className, id, type: 'button', ariaLabel: label, title: label }, icon(glyph));
    this.el.addEventListener('pointerdown', this.onDown);
    // Not `pointerleave`: a thumb drifts, and a held button that lets go when it drifts a few
    // pixels over its edge is one you cannot hold. Capture keeps the finger's events coming
    // here wherever it goes, and up or cancel is the only thing that ends a hold.
    this.el.addEventListener('pointerup', this.onUp);
    this.el.addEventListener('pointercancel', this.onUp);
  }

  private readonly onDown = (e: PointerEvent): void => {
    if (this.pointerId !== null) return;
    this.pointerId = e.pointerId;
    this.pressed = true;
    this.el.classList.add('is-active');
    capturePointer(this.el, e.pointerId);
    // Stops the button also firing a synthetic click/touch-scroll — see the canvas's own
    // `touch-action: none` in styles.css, which this mirrors for the control surfaces.
    e.preventDefault();
  };

  private readonly onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.release();
  };

  /** Forces the button up without a matching pointer event — the blur/visibility escape
   *  hatch, same job as `InputController`'s own `onBlur`. */
  release(): void {
    this.pointerId = null;
    this.pressed = false;
    this.el.classList.remove('is-active');
  }

  dispose(): void {
    this.el.removeEventListener('pointerdown', this.onDown);
    this.el.removeEventListener('pointerup', this.onUp);
    this.el.removeEventListener('pointercancel', this.onUp);
  }
}

/** A tap button: fires once on release, not while held. Used for respawn and the camera
 *  toggle, which are one-shot actions on the keyboard too. */
class TapButton {
  readonly el: HTMLButtonElement;
  private pointerId: number | null = null;

  constructor(
    className: string,
    id: string,
    glyph: IconName,
    label: string,
    private readonly onTap: () => void,
  ) {
    this.el = el('button', { class: className, id, type: 'button', ariaLabel: label, title: label }, icon(glyph));
    this.el.addEventListener('pointerdown', this.onDown);
    this.el.addEventListener('pointerup', this.onUp);
    this.el.addEventListener('pointercancel', this.onCancel);
    this.el.addEventListener('pointerleave', this.onCancel);
  }

  private readonly onDown = (e: PointerEvent): void => {
    if (this.pointerId !== null) return;
    this.pointerId = e.pointerId;
    this.el.classList.add('is-active');
    capturePointer(this.el, e.pointerId);
    e.preventDefault();
  };

  private readonly onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.pointerId = null;
    this.el.classList.remove('is-active');
    this.onTap();
  };

  /** Cancelled rather than fired — a finger that slid off before lifting didn't mean it. */
  private readonly onCancel = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.pointerId = null;
    this.el.classList.remove('is-active');
  };

  release(): void {
    this.pointerId = null;
    this.el.classList.remove('is-active');
  }

  dispose(): void {
    this.el.removeEventListener('pointerdown', this.onDown);
    this.el.removeEventListener('pointerup', this.onUp);
    this.el.removeEventListener('pointercancel', this.onCancel);
    this.el.removeEventListener('pointerleave', this.onCancel);
  }
}

/**
 * The steering pad. The anchor is wherever the thumb first lands, not a fixed point on
 * screen — a thumb's natural rest position on a phone varies with hand size and grip, and
 * pinning the anchor would fight whichever guess was wrong. Only horizontal displacement
 * counts; the physics has no use for vertical.
 */
class Stick {
  readonly el: HTMLElement;
  private readonly base: HTMLElement;
  private readonly nub: HTMLElement;
  private pointerId: number | null = null;
  private anchorX = 0;
  value = 0;

  constructor() {
    this.base = el('div', { class: 'tc-stick-base' });
    this.nub = el('div', { class: 'tc-stick-nub' });
    this.el = el('div', { class: 'tc-stick-zone', id: 'touch-steer' }, this.base, this.nub);
    this.el.addEventListener('pointerdown', this.onDown);
    this.el.addEventListener('pointermove', this.onMove);
    // Not `pointerleave`, for the reason in `HoldButton`: full lock is a thumb's width to one
    // side, and that can be off the edge of the zone.
    this.el.addEventListener('pointerup', this.onUp);
    this.el.addEventListener('pointercancel', this.onUp);
  }

  private readonly onDown = (e: PointerEvent): void => {
    if (this.pointerId !== null) return;
    this.pointerId = e.pointerId;
    this.anchorX = e.clientX;
    const rect = this.el.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    this.base.style.left = `${localX}px`;
    this.base.style.top = `${localY}px`;
    this.nub.style.left = `${localX}px`;
    this.nub.style.top = `${localY}px`;
    this.nub.style.transform = 'translate(-50%, -50%)';
    this.el.classList.add('is-active');
    capturePointer(this.el, e.pointerId);
    e.preventDefault();
  };

  private readonly onMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    const dx = e.clientX - this.anchorX;
    // Positive steer is a left turn (see CarInput), so a rightward drag — positive dx —
    // has to come out negative.
    this.value = -applyDeadzone(dx, STEER_DEADZONE_PX, STEER_RANGE_PX);
    const nubOffset = Math.max(-STEER_RANGE_PX, Math.min(STEER_RANGE_PX, dx));
    this.nub.style.transform = `translate(calc(-50% + ${nubOffset}px), -50%)`;
  };

  private readonly onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.release();
  };

  release(): void {
    this.pointerId = null;
    this.value = 0;
    this.el.classList.remove('is-active');
    this.nub.style.transform = 'translate(-50%, -50%)';
  }

  dispose(): void {
    this.el.removeEventListener('pointerdown', this.onDown);
    this.el.removeEventListener('pointermove', this.onMove);
    this.el.removeEventListener('pointerup', this.onUp);
    this.el.removeEventListener('pointercancel', this.onUp);
  }
}

type Pedal = 'throttle' | 'brake' | 'handbrake';

/**
 * Pixels around each pedal that still count as on it. A thumb pressing a pedal it cannot see
 * lands a little off it as often as on it, and the gap between two pedals belongs to
 * whichever is nearer rather than to nothing.
 */
const PEDAL_SLOP_PX = 14;

/**
 * Throttle, brake and handbrake, as one surface rather than three buttons.
 *
 * Each finger on it presses whatever pedal it is over now, not the one it came down on, so a
 * thumb slides from the throttle onto the brake and back without lifting — which is how a
 * phone racer is driven — and a finger that drifts off a pedal lets go of it only until it
 * drifts back. As separate buttons, a thumb that slid a few pixels over an edge dropped the
 * throttle with nothing on screen to say why. Two fingers can hold two pedals.
 */
class Pedals {
  readonly el: HTMLElement;
  private readonly pads: Record<Pedal, HTMLElement>;
  /** Each finger on the surface, and the pedal it is over, if any. */
  private readonly fingers = new Map<number, Pedal | null>();

  constructor() {
    const pad = (pedal: Pedal, id: string, label: string, content: string) =>
      el('div', { class: `tc-pedal tc-${pedal}`, id, role: 'button', ariaLabel: label, title: label }, content);
    this.pads = {
      throttle: pad('throttle', 'touch-throttle', t('Throttle'), '▲'),
      brake: pad('brake', 'touch-brake', t('Brake'), '▼'),
      handbrake: pad('handbrake', 'touch-handbrake', t('Handbrake'), 'HB'),
    };
    this.el = el('div', { class: 'tc-pedals', id: 'touch-pedals' }, this.pads.handbrake, this.pads.brake, this.pads.throttle);
    this.el.addEventListener('pointerdown', this.onDown);
    this.el.addEventListener('pointermove', this.onMove);
    this.el.addEventListener('pointerup', this.onUp);
    this.el.addEventListener('pointercancel', this.onUp);
  }

  pressed(pedal: Pedal): boolean {
    for (const on of this.fingers.values()) if (on === pedal) return true;
    return false;
  }

  /** The pedal under a point, the nearest where the slop of two overlaps. */
  private at(x: number, y: number): Pedal | null {
    let best: Pedal | null = null;
    let bestDistance = Infinity;
    for (const pedal of Object.keys(this.pads) as Pedal[]) {
      const r = this.pads[pedal].getBoundingClientRect();
      const dx = Math.max(r.left - x, 0, x - r.right);
      const dy = Math.max(r.top - y, 0, y - r.bottom);
      const distance = Math.hypot(dx, dy);
      if (distance <= PEDAL_SLOP_PX && distance < bestDistance) {
        best = pedal;
        bestDistance = distance;
      }
    }
    return best;
  }

  private readonly onDown = (e: PointerEvent): void => {
    this.fingers.set(e.pointerId, this.at(e.clientX, e.clientY));
    this.paint();
    capturePointer(this.el, e.pointerId);
    e.preventDefault();
  };

  private readonly onMove = (e: PointerEvent): void => {
    if (!this.fingers.has(e.pointerId)) return;
    const on = this.at(e.clientX, e.clientY);
    if (on === this.fingers.get(e.pointerId)) return;
    this.fingers.set(e.pointerId, on);
    this.paint();
  };

  private readonly onUp = (e: PointerEvent): void => {
    if (!this.fingers.delete(e.pointerId)) return;
    this.paint();
  };

  private paint(): void {
    for (const pedal of Object.keys(this.pads) as Pedal[]) {
      this.pads[pedal].classList.toggle('is-active', this.pressed(pedal));
    }
  }

  release(): void {
    this.fingers.clear();
    this.paint();
  }

  dispose(): void {
    this.el.removeEventListener('pointerdown', this.onDown);
    this.el.removeEventListener('pointermove', this.onMove);
    this.el.removeEventListener('pointerup', this.onUp);
    this.el.removeEventListener('pointercancel', this.onUp);
  }
}

/**
 * Whether a touch is on the race rather than on something that scrolls or takes text — the
 * Escape menu, the results, the chat box. Only those are left to the browser.
 */
function onRaceSurface(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('.touch-controls, .hud, canvas') !== null && target.closest('.results') === null;
}

export class TouchControls {
  readonly root: HTMLElement;

  private readonly stick = new Stick();
  private readonly pedals = new Pedals();
  private readonly lookBack = new HoldButton('tc-btn tc-lookback', 'touch-lookback', 'visibility', t('Look behind'));
  private readonly respawnBtn: TapButton;
  private readonly cameraBtn: TapButton;
  private readonly honkBtn: TapButton;
  private readonly menuBtn: TapButton;

  private readonly onWindowBlur = (): void => this.reset();
  private readonly onVisibility = (): void => {
    if (document.hidden) this.reset();
  };
  /**
   * Safari zooms the page on a pinch and scrolls it on a drag wherever `touch-action` lets it,
   * and two thumbs on the glass are a pinch as far as it knows. A page that zoomed or scrolled
   * under the race left the controls somewhere the thumbs were not.
   */
  private readonly onTouchMove = (e: TouchEvent): void => {
    if (onRaceSurface(e.target)) e.preventDefault();
  };
  private readonly onGesture = (e: Event): void => {
    if (onRaceSurface(e.target)) e.preventDefault();
  };

  constructor(options: TouchControlsOptions) {
    this.respawnBtn = new TapButton('tc-btn tc-respawn', 'touch-respawn', 'restart_alt', t('Respawn on the route'), options.onRespawn);
    this.cameraBtn = new TapButton('tc-btn tc-camera', 'touch-camera', 'videocam', t('Chase / aerial camera'), options.onToggleCamera);
    this.honkBtn = new TapButton('tc-btn tc-honk', 'touch-honk', 'campaign', t('Horn'), options.onHonk);
    this.menuBtn = new TapButton('tc-btn tc-menu', 'touch-menu', 'menu', t('Menu'), options.onMenu);

    const utility = el(
      'div',
      { class: 'tc-utility' },
      this.lookBack.el,
      this.respawnBtn.el,
      this.cameraBtn.el,
      this.honkBtn.el,
      this.menuBtn.el,
    );
    const rotateHint = el(
      'div',
      { class: 'tc-rotate-hint' },
      el('span', { class: 'tc-rotate-icon' }, '⟳'),
      el('span', {}, t('Rotate your device to race')),
    );

    this.root = el(
      'div',
      { class: 'touch-controls', id: 'touch-controls' },
      this.stick.el,
      this.pedals.el,
      utility,
      rotateHint,
    );

    // Hides the keyboard hint and gives the status pill room to sit above the pedals — see
    // touchControls.css. Scoped to a class rather than touched in styles.css directly.
    document.documentElement.classList.add('touch-active');
    window.addEventListener('blur', this.onWindowBlur);
    document.addEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('touchmove', this.onTouchMove, { passive: false });
    document.addEventListener('gesturestart', this.onGesture);
  }

  sample(): CarInput {
    return {
      throttle: (this.pedals.pressed('throttle') ? 1 : 0) - (this.pedals.pressed('brake') ? 1 : 0),
      steer: this.stick.value,
      handbrake: this.pedals.pressed('handbrake'),
    };
  }

  get braking(): boolean {
    return this.pedals.pressed('brake');
  }

  get lookingBack(): boolean {
    return this.lookBack.pressed;
  }

  /** Zeroes every held control without waiting for the pointer events that normally do it —
   *  a phone call mid-corner delivers a blur or a visibility change, not a pointerup. */
  reset(): void {
    this.stick.release();
    this.pedals.release();
    this.lookBack.release();
    this.respawnBtn.release();
    this.cameraBtn.release();
    this.honkBtn.release();
    this.menuBtn.release();
  }

  dispose(): void {
    window.removeEventListener('blur', this.onWindowBlur);
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('touchmove', this.onTouchMove);
    document.removeEventListener('gesturestart', this.onGesture);
    this.stick.dispose();
    this.pedals.dispose();
    this.lookBack.dispose();
    this.respawnBtn.dispose();
    this.cameraBtn.dispose();
    this.honkBtn.dispose();
    this.menuBtn.dispose();
    document.documentElement.classList.remove('touch-active');
    this.root.remove();
  }
}
